import isString from 'lodash/isString';
import Q from 'q';

import API from 'js/lib/api';
import memoize from 'js/lib/memoize';
import path from 'js/lib/path';

import type { UserEmail } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/email_address';
import type { InsertUserEmailRequest } from '@coursera/grpc-types-useremails/coursera/proto/useremails/v1/user_emails_api';

import { createApolloClient } from 'bundles/page/lib/network/Apollo';
import constants from 'bundles/third-party-auth/constants';
import ThirdPartyAuthUpdateFullNameMutation from 'bundles/third-party-auth/queries/ThirdPartyAuthUpdateFullNameMutation.graphql';
import type {
  ThirdPartyAuthUpdateFullNameMutationMutation,
  ThirdPartyAuthUpdateFullNameMutationMutationVariables,
} from 'bundles/third-party-auth/queries/__generated__/ThirdPartyAuthUpdateFullNameMutation';
import { type CheckForcedSsoLoginResponse } from 'bundles/third-party-auth/types/responseTypes';

const api = API('/api', { type: 'rest' });

const _apiCall = function (method: keyof typeof api, ...args: $TSFixMe) {
  // @ts-expect-error ts-migrate(2349) FIXME: This expression is not callable.
  return Q(api[method](...args))
    .then((response) => {
      try {
        return isString(response) ? JSON.parse(response) : response;
      } catch {
        return response;
      }
    })
    .catch((xhr) => {
      try {
        return Q.reject(JSON.parse(xhr.responseText));
      } catch {
        return Q.reject({ code: xhr.responseText });
      }
    });
};

const exported = {
  list: memoize(function () {
    return _apiCall('get', constants.naptimeResource);
  }),

  login(
    data: $TSFixMe,
    queryParams?: Record<string, string | boolean>
  ): Q.Promise<{ isRegistration: boolean; userId: number }> {
    const apiPath = queryParams
      ? `${constants.authApi}?${new URLSearchParams(queryParams as Record<string, string>).toString()}`
      : constants.authApi;
    return _apiCall('post', apiPath, { data }) as $TSFixMe;
  },

  link(data: $TSFixMe) {
    return _apiCall('post', constants.linkApi, { data });
  },

  delink(id: $TSFixMe, data: $TSFixMe) {
    const apiPath = path.join(constants.naptimeResource, id);
    return _apiCall('delete', apiPath, { data });
  },

  addAlternateEmail(data: InsertUserEmailRequest) {
    const apiPath = path.join(constants.userEmailsApi, 'InsertUserEmail');
    return _apiCall('post', apiPath, { data });
  },

  findIdFromEmail(email: string): Q.Promise<{ userEmail: UserEmail }> {
    const apiPath = path.join(constants.userEmailsApi, 'GetUserEmail');
    const data = { emailAddress: email };
    return _apiCall('post', apiPath, { data }) as $TSFixMe;
  },

  async updateFullName(fullName: string) {
    const apolloClient = createApolloClient();

    const { data, errors } = await apolloClient.mutate<
      ThirdPartyAuthUpdateFullNameMutationMutation,
      ThirdPartyAuthUpdateFullNameMutationMutationVariables
    >({
      mutation: ThirdPartyAuthUpdateFullNameMutation,
      variables: { input: { fullName } },
      context: { clientName: 'gatewayGql' },
    });

    if (errors) {
      throw new Error(errors.map((error) => error.message).join(', '));
    }

    if (data?.LearnerObject_UpdateFullName?.__typename === 'LearnerObject_UpdateFullNameResultError') {
      throw new Error(data.LearnerObject_UpdateFullName.message);
    }
  },

  checkForcedSsoLogin(email: string, returnTo?: string, options?: { preferredOrgIds?: string[] }) {
    const data = {
      email,
      returnTo: returnTo || '/',
      preferredOrgIds: options?.preferredOrgIds,
    };
    const apiPath = constants.checkForcedSsoLoginApi;
    return _apiCall('post', apiPath, { data }) as unknown as Q.Promise<CheckForcedSsoLoginResponse>;
  },
};

export default exported;

export const { list, login, link, delink, addAlternateEmail, findIdFromEmail, checkForcedSsoLogin } = exported;
